import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { controlPropTypes } from "../../formhelpers";
import DropdownMenu from "./DropdownMenu";
import { processKeyName } from "../utils/keyEvents";

export const MISSING = {VALUE: "_NULL", LABEL: "Missing"};
export default class TypeaheadCore extends React.Component {
    constructor(props) {
        super(props);
        // input bindings
        this.inputRef = React.createRef();
        this.onInputFocus = this.onInputFocus.bind(this);
        this.onInputBlur = this.onInputBlur.bind(this);
        this.onInputKeyDown = this.onInputKeyDown.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.onRecordbrowserClick = this.onRecordbrowserClick.bind(this);

        // typeahead bindings
        this.typeaheadRef = React.createRef();
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onClearValue = this.onClearValue.bind(this);
        this.onSelectAll = this.onSelectAll.bind(this);

        // dropdown bindings
        this.onSelect = this.onSelect.bind(this);
        this.onSort = this.onSort.bind(this);
        this.hideDropdownMenu = this.hideDropdownMenu.bind(this);
        this.showDropdownMenu = this.showDropdownMenu.bind(this);

        this.state = {
            searchText: "",
            focused: false, // if typeahead is currently focused.
            showDropdownMenu: false, // if dropdown menu is currently opened.
            selectedActiveIndex: null, // for multiselect backspace key
            sort: {
                key: "",
                dir: ""
            },
        };
    }

    onRecordbrowserClick(e) {
        const {onRecordbrowserClick, descriptor} = this.props;
        onRecordbrowserClick && onRecordbrowserClick(e, descriptor);
    }

    onSearch(e) {
        const { onSearch } = this.props;
        this.setState({searchText: e.target.value, showDropdownMenu: true, selectedActiveIndex: null});
        onSearch && onSearch(e.target.value || "", this.state.sort);
    }

    onInputKeyDown(e) {
        const { descriptor, onKeyDown, value, multiValued } = this.props;
        const {searchText, selectedActiveIndex} = this.state;
        let newValue = value;
        if(multiValued) {
            newValue = Array.isArray(value) ? [...value] : [];
        }
        const keyDownName = processKeyName(e.key);
        const openKeys = multiValued && newValue.length ? ["ArrowDown"] : ["ArrowDown", "ArrowLeft", "ArrowRight"];
        if(!this.state.showDropdownMenu && openKeys.includes(keyDownName)) {
            e.preventDefault();
            e.stopPropagation();
            this.showDropdownMenu();
            return;
        }
        else if(multiValued && ["ArrowLeft", "ArrowRight", "Backspace", "Escape"].includes(keyDownName)) {
            if(this.state.showDropdownMenu && (keyDownName === "Backspace" && searchText.length === 0) ) {
                this.hideDropdownMenu();
            }
            // multiValued allows removing items with the backspace.
            // use the Left/Right arrow keys to highlight a field.
            if(keyDownName === "Backspace" && newValue.length && searchText.length === 0) {
                if(selectedActiveIndex === null) {
                    this.setState({selectedActiveIndex: newValue.length-1});
                }
                else {
                    newValue.splice(selectedActiveIndex, 1);// remove the active item.
                    this.onChange(newValue.length ? newValue : null);
                    this.setState({selectedActiveIndex: null});
                }
            }
            else if(["ArrowLeft", "ArrowRight"].includes(keyDownName) && newValue.length) {
                let newActiveItem = selectedActiveIndex;
                if(newActiveItem == null) { newActiveItem = -1; }
                (keyDownName === "ArrowLeft") ? newActiveItem-- : newActiveItem++;
                if(newActiveItem < 0) { newActiveItem = newValue.length-1; }
                if(newActiveItem >= newValue.length) { newActiveItem = 0; }
                this.setState({selectedActiveIndex: newActiveItem});
            }
            else if(keyDownName === "Escape") {
                this.setState({selectedActiveIndex: null});
            }
        }
        if(keyDownName === "Enter") {
            if(this.state.showDropdownMenu) {
                // if the dropdown is open on enter do not advance to the next control.
                return;
            }
        }
        onKeyDown && onKeyDown(e, descriptor, newValue);
    }

    onInputFocus(e) {
        const { descriptor, onFocus, value } = this.props;
        if(!this.state.focused) {
            // only trigger focus once when changing from unfocused.
            this.setState({focused: true});
            onFocus && onFocus(e, descriptor, value);
        }
    }

    onInputBlur(e) {
        const { descriptor, onBlur, value } = this.props;
        if(this.state.focused && !this.state.showDropdownMenu) {
            this.setState({focused: false, selectedActiveIndex: null});
            onBlur && onBlur(e, descriptor, value);
        }
    }

    onClick(e) {
        const { enabled, descriptor, onClick, onFocus, value } = this.props;
        if(enabled) {
            if(!this.state.focused) {
                // only trigger focus once when changing from unfocused.
                this.setState({focused: true});
                onFocus && onFocus(e, descriptor, value);
            }
            // show options menu.
            this.showDropdownMenu();
        }
        onClick && onClick(e, descriptor, value);
    }

    onSelect(item) {
        const {value, valueKey, multiValued} = this.props;
        let newValue;
        if(multiValued) {
            if(value === null || item.atomic) {
                newValue = [];
            }
            else {
                // we can never add a new selection to an atomic option
                newValue = value.some(it => it.atomic) ? [] : [...value];
            }
            const index = newValue.findIndex((v) => v[valueKey] == item[valueKey]);
            if(index !== -1) {
                // remove the selected value
                newValue.splice(index, 1);
                if(newValue.length === 0) {
                    newValue = null;
                }
            }
            else {
                // add the selected value
                newValue.push({...item});
            }
        }
        else {
            newValue = {...item};
        }
        this.onChange(newValue);
    }

    onSort(sort) {
        const {onSearch} = this.props;
        const {searchText} = this.state;
        this.setState({sort: sort}, () => {
            this.inputRef.current.focus();
        });
        onSearch && onSearch(searchText, sort);
    }

    onRemoveItem(e, item, index) { // multi only event
        e.preventDefault();
        e.stopPropagation();
        const {value = [], valueKey, multiValued} = this.props;
        if(multiValued) {
            const newValue = [...value];
            if (newValue[index][valueKey] == item[valueKey]) { // allow string/number coercions
                // selected item aligns with prop values
                // ok to remove
                newValue.splice(index, 1);
                this.onChange(newValue.length ? newValue : null);
            }
        }
    }

    onClearValue() {
        this.onChange(null);
    }

    onSelectAll() {
        const {
            options,
        } = this.props;
        this.onChange(options.filter(it => !it.atomic));
    }

    onChange(newValue) {
        const {descriptor, onCommitChange, enabled} = this.props;
        enabled && onCommitChange && onCommitChange(descriptor, newValue);
    }

    showDropdownMenu() {
        this.setState({focused: true, showDropdownMenu: true, searchText: ""}, () => {
            this.inputRef.current.focus();
        });
    }

    hideDropdownMenu() {
        const { onSearch } = this.props;
        this.setState({focused: true, showDropdownMenu: false, searchText: ""}, () => {
            this.inputRef.current.focus();
        });
        onSearch && onSearch("", this.state.sort); // reset dropdown results
    }

    convertValueToSelected(value) {
        const {addMissing, multiValued, valueKey, labelKey} = this.props;
        if(multiValued) {
            if(value === undefined || value === null) {
                return [];
            }
            return value;
        }
        else {
            if(addMissing && (value === undefined || value === null)) {
                return {
                    [valueKey]: MISSING.VALUE,
                    [labelKey]: MISSING.LABEL
                };
            }
            return value;
        }
    }

    render() {
        const {
            multiValued,
            value,
            enabled,
            labelKey,
            showRecordBrowserIcon,
            showSelectAll,
            autoSelectFirstItem,
            isLoading,
            isPartial,
            isError,
            isServer,
            options,
        } = this.props;

        const {
            focused,
            searchText,
            showDropdownMenu,
            sort,
            selectedActiveIndex
        } = this.state;
        const showInput = searchText.length > 0;

        const selected = this.convertValueToSelected(value);
        const hasValue = multiValued ? selected.length > 0 : (selected !== undefined && selected !== null);

        const classnames = classNames({
            typeahead: true,
            active: focused,
            disabled: !enabled,
            multiselect: multiValued,
            open: showDropdownMenu
        });
        return (
            <div ref={this.typeaheadRef} className={showRecordBrowserIcon ? "input-group" : ""}>
                <div
                    className={classnames}
                    data-loading={isLoading}
                    data-partial={isPartial !== undefined ? isPartial : options.length > 100}
                    data-error={isError}
                    data-open={showDropdownMenu}
                    data-count={options.length}
                    onClick={this.onClick}
                >
                    <input
                        ref={this.inputRef}
                        onFocus={this.onInputFocus}
                        onBlur={this.onInputBlur}
                        onKeyDown={this.onInputKeyDown}
                        onChange={this.onSearch}
                        disabled={!enabled}
                        className="form-control focusable"
                        type="text"
                        style={showInput ? {opacity: 1, width: "calc(100% - 20px)"} : {opacity: 0, position: "absolute", zIndex: -1}}
                        value={searchText}
                    />
                    <div className="pull-right"><span className="caret"/></div>
                    {(!hasValue && !showInput && focused) && <div className="no-query-label">Enter a search term</div>}

                    {multiValued ? (<div>
                        {selected.map((item, index) => (<span key={index} className={`item-selection ${ index === selectedActiveIndex ? "active" : ""}`}>{item[labelKey]}<span
                            className="item-selection-close" onClick={(e) => this.onRemoveItem(e, item, index)}><span
                            className="item-selection-close-inner">×</span></span>
                        </span>))}
                    </div>) : hasValue && (<div className="single-item-selection">{selected[labelKey]}</div>)}
                </div>
                {showRecordBrowserIcon && enabled && <a href="#" className="input-group-addon icon-search" onClick={this.onRecordbrowserClick}><span/></a>}
                {(!autoSelectFirstItem && !multiValued) && <a href="#" className="typeahead-icon-button clear-value" onClick={this.onClearValue}><span/></a>}
                {(showSelectAll && multiValued) && <a href="#" title="Select all options" className="typeahead-icon-button select-all" onClick={this.onSelectAll}><span/></a>}
                <DropdownMenu
                    {...this.props}
                    targetRef={this.typeaheadRef}
                    show={showDropdownMenu}
                    onHide={this.hideDropdownMenu}
                    onSelect={this.onSelect}
                    onSort={this.onSort}
                    sort={sort}
                    searchText={searchText}
                    selected={selected}
                />
                {(!isServer && isLoading) && <div className="local-over-modal">Searching...</div>}
            </div>
        );

    }
}

TypeaheadCore.defaultProps = {
    options: [],
    columns: [],
    valueKey: "value",
    labelKey: "label",
};

TypeaheadCore.propTypes = {
    ...controlPropTypes(),
    onSearch: PropTypes.func,

    // Value is assumed to be the inflated value(s) or null. Meaning an object
    // with {[valueKey]: ... , [labelKey]: ... } or array of those objects.
    // This is different from how the jQuery control worked which only supplied
    // the value string(s) and expected the inflated value to exist in the
    // options list.
    value:  PropTypes.oneOfType([PropTypes.object,PropTypes.arrayOf(PropTypes.object)]),

    options: PropTypes.arrayOf(PropTypes.object.isRequired),
    columns: PropTypes.arrayOf(PropTypes.shape({
        includeInFilter: PropTypes.bool,
        isSortable: PropTypes.bool,
        key: PropTypes.string.isRequired,
        label: PropTypes.string,
    })).isRequired,
    valueKey: PropTypes.string.isRequired,
    labelKey: PropTypes.string.isRequired,
    dropdownLength: PropTypes.number,
    addMissing: PropTypes.bool,
    multiValued: PropTypes.bool,
    statusMessage: PropTypes.string,
    showRecordBrowserIcon: PropTypes.bool,
    showSelectAll: PropTypes.bool,
    autoSelectFirstItem: PropTypes.bool,
    keepSelectionInList: PropTypes.bool,
    isServer: PropTypes.bool,
    isLoading: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./app/lib/common/descriptorcontrols/BaseControls/Typeahead/Core.js